import axios from "axios";
import * as _ from "lodash";
import { MESSAGE_STATE } from "@/store/FlashMessages/state";
import store from "@/store/store";
import { getItem } from "@/store/persistantStorage";
import { mutationTypes as flashMessagesMutationTypes } from "@/store/FlashMessages/types";
import { actionTypes, actionTypes as loggedInUserActionTypes } from "@/store/LoggedUser/types";
import { mutationTypes as dialogMutationTypes } from "@/store/Dialog/types";
import { LS_KEYS } from "@/store/Publisher/types";

function isUrlWithApiKey(url) {
    return !(
        url === "https://cdn.dopc.cz/js/stat-loader.min.js" ||
        url.includes("login") ||
        url.includes("password_recovery") ||
        url.includes("verify") ||
        url.includes("register")
    );
}

function isStatLoader(url) {
    return url === "https://cdn.dopc.cz/js/stat-loader.min.js";
}

function isLogin(url) {
    return url.includes("login");
}

axios.defaults.baseURL = process.env.VUE_APP_API_URL + "/api/v1";
// console.log("baseURL", axios.defaults.baseURL);
axios.defaults.headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

function getActivePublisherId() {
    const mode = getItem(LS_KEYS.MODE) || "ltba";
    const key = mode === "yield" ? LS_KEYS.PUBLISHERS_YIELD : LS_KEYS.PUBLISHERS_LTBA;
    const arr = getItem(key);
    if (Array.isArray(arr) && arr.length > 0 && arr[0]?.id != null) {
        return arr[0].id;
    }
    // Fallback: backend requires a valid integer X-Publisher. Before the user has
    // explicitly picked a publisher (first login, before /sql/publishers response),
    // use the user's first_publisher_id from the login response.
    const user = getItem("loggedinuser");
    if (user?.first_publisher_id != null) return user.first_publisher_id;
    return null;
}

axios.interceptors.request.use(function (config) {
    // SET Authorization header
    if (config.url && isUrlWithApiKey(config.url)) {
        const authConfig = config;
        const user = getItem("loggedinuser");
        authConfig.headers["Authorization"] = _.get(user, "api_key");
        // Only send X-Publisher when we have a numeric id — the backend rejects
        // "null" / empty strings with a 792 "invalid literal for int()".
        const pubId = getActivePublisherId();
        if (pubId !== null && pubId !== undefined) {
            authConfig.headers["X-Publisher"] = pubId;
        }
        return authConfig;
    } else {
        return config;
    }
});

axios.interceptors.response.use(
    function (response) {
        // Dont close the modal for permission operations
        const isPermissionOperation =
            response.config.url &&
            (response.config.url.includes("add-permission") || response.config.url.includes("remove-permission"));

        if ((response.data.success || response.data.ok) && !isPermissionOperation) {
            store.commit(dialogMutationTypes.closeDialog);
        }
        const bothVersions = response.headers["x-api-version"];
        if (bothVersions) {
            const versions = bothVersions.split(",");
            const apiVersion = versions[0];
            const adminVersion = versions[1];
            const requestUrl = response.request.responseURL;
            if (!isStatLoader(requestUrl)) {
                if (0 && localStorage.getItem("adminversion") !== adminVersion && apiVersion !== "debug") {
                    store.dispatch(actionTypes.logout);
                    store.commit(flashMessagesMutationTypes.addMessageToQueue, {
                        header: "Please login again.",
                        description: "Version of application has been updated.",
                        messageState: MESSAGE_STATE.INFO,
                    });
                } else if (isLogin(requestUrl)) {
                    store.dispatch(actionTypes.setUserVersionToLS, bothVersions);
                } else if (isUrlWithApiKey(requestUrl)) {
                    const user = getItem("loggedinuser");
                    if (user.version !== bothVersions) {
                        store.dispatch(actionTypes.logout);
                        store.commit(flashMessagesMutationTypes.addMessageToQueue, {
                            header: "Please login again.",
                            description: "Version of application has been updated.",
                            messageState: MESSAGE_STATE.INFO,
                        });
                    }
                }
            }
        }

        return response;
    },
    function (error) {
        if (error.response && error.response.status === 401) {
            // Unauthorized — but a 401 from the login endpoint is a failed sign-in,
            // handled by the login action itself; dispatching logout here would
            // reset the store and wipe the login form error state.
            if (!isLogin(error.config?.url || "")) {
                store.commit(flashMessagesMutationTypes.addMessageToQueue, {
                    header: error?.response?.data?.title,
                    description: error?.response?.data?.description,
                    messageState: MESSAGE_STATE.INFO,
                });
                store.dispatch(loggedInUserActionTypes.logout);
            }
        } else if (!error?.response?.data?.error) {
            // error.response.data.error slouzi k vykresleni erroru ve formu, ne do flash message
            const message = {
                header: error?.response?.data?.title,
                description: error?.response?.data?.description,
                messageState: MESSAGE_STATE.WARN,
            };
            store.commit(flashMessagesMutationTypes.addMessageToQueue, message);
        }
        return Promise.reject(error);
    }
);

export default axios;
